(function () {
  'use strict';

  function syncViewportForStep(instance, stepEl) {
    if (!stepEl || typeof window === 'undefined') return;
    // Single scroll context: just bring the section to the top of the
    // viewport. The browser handles everything else natively.
    instance.scrollIntoView({ block: 'start', behavior: 'auto' });
  }

  function showStep(instance, name, direction) {
    const nextEl = instance.querySelector(`[data-step="${name}"]`);
    if (!nextEl) return;

    instance._log('step-activate', { from: instance.currentStep, to: name, direction });

    const enterClass = direction === 'forward' ? 'consultation-step--enter-right' : 'consultation-step--enter-left';

    instance.querySelectorAll('.consultation-step').forEach((step) => {
      if (step === nextEl) return;
      step.inert = true;
      step.hidden = true;
      step.setAttribute('aria-hidden', 'true');
      step.classList.remove(
        'consultation-step--exit-left',
        'consultation-step--exit-right',
        'consultation-step--enter-left',
        'consultation-step--enter-right'
      );
      disableStepControls(step);
    });

    nextEl.hidden = false;
    nextEl.inert = false;
    nextEl.removeAttribute('aria-hidden');
    nextEl.style.display = '';
    enableStepControls(nextEl);
    nextEl.classList.add(enterClass);
    requestAnimationFrame(function onFrame() {
      requestAnimationFrame(function afterFrame() {
        nextEl.classList.remove(enterClass);
      });
    });

    instance.currentStep = name;
    updateProgress(instance);
    instance._syncAnchorOnboarding();
    syncViewportForStep(instance, nextEl);

    const visibleSteps = [...instance.querySelectorAll('.consultation-step')].filter((step) => !step.hidden);
    if (visibleSteps.length !== 1) {
      console.error('[Tara Consultation] Step invariant broken:', visibleSteps.map((step) => step.dataset.step));
      visibleSteps.forEach((step) => {
        if (step !== nextEl) {
          step.hidden = true;
          step.inert = true;
          step.setAttribute('aria-hidden', 'true');
        }
      });
    }

    if (instance.backBtn) {
      instance.backBtn.hidden = (name === 'anchor' || name === 'assessment-loading');
    }
    if (instance.kbdHint) {
      instance.kbdHint.style.display = (name === 'anchor' || name === 'adaptive') ? '' : 'none';
    }
  }

  function disableStepControls(stepEl) {
    stepEl.querySelectorAll('button, textarea, input, [role="radio"]').forEach((el) => {
      el.disabled = true;
      el.setAttribute('tabindex', '-1');
    });
    stepEl.querySelectorAll('.consultation-options').forEach((el) => {
      el.style.pointerEvents = 'none';
    });
  }

  function enableStepControls(stepEl) {
    stepEl.querySelectorAll('button, textarea, input, [role="radio"]').forEach((el) => {
      el.disabled = false;
      el.removeAttribute('tabindex');
    });
    stepEl.querySelectorAll('.consultation-options').forEach((el) => {
      el.style.pointerEvents = '';
      el.style.opacity = '';
    });
  }

  function goBack(instance) {
    if (instance.currentStep === 'email') {
      instance._resetAssessmentState?.();
      // The last adaptive response is the completion payload at this point and
      // has no answerable question. Remove the completing turn, then ask the
      // worker to return the previous real question.
      if (instance.conversationHistory.length > 1) {
        instance.conversationHistory.pop();
        instance.questionCount--;
        instance._setConversationHistory(instance.conversationHistory);
      }
      instance._showStep('adaptive', 'back');
      instance._fetchNextQuestion('back');
      return;
    }

    if (instance.currentStep !== 'adaptive') return;

    if (instance.conversationHistory.length > 1) {
      instance.conversationHistory.pop();
      instance.questionCount--;
      instance._setConversationHistory(instance.conversationHistory);
    }

    if (instance.conversationHistory.length <= 1) {
      instance._showStep('anchor', 'back');
      const firstTurn = instance.conversationHistory[0];
      const optionValue = firstTurn?.selected_value || (firstTurn?.input_type === 'option' ? firstTurn?.answer : null);
      if (optionValue) {
        instance._anchorSelectedValue = optionValue;
        instance._anchorSelectedLabel = firstTurn?.selected_label || firstTurn?.answer_label || optionValue;
        const anchorStep = instance.querySelector('[data-step="anchor"]');
        anchorStep?.querySelectorAll('.consultation-option').forEach((option) => {
          const selected = option.dataset.value === optionValue;
          option.classList.toggle('consultation-option--selected', selected);
          option.setAttribute('aria-checked', selected ? 'true' : 'false');
        });
      }
      if (firstTurn?.input_type === 'free_text' && firstTurn?.answer) {
        const intakeText = instance.querySelector('[data-intake-text]');
        if (intakeText) intakeText.value = firstTurn.answer;
      }
      return;
    }

    instance._fetchNextQuestion('back');
  }

  function updateProgress(instance) {
    const t = instance.config?.i18n || {};
    const phases = {
      anchor: { label: t.phaseUnderstanding || 'Understanding your hair and scalp', pct: 10 },
      adaptive: { label: getPhaseLabel(instance), pct: Math.min(15 + instance.questionCount * 14, 85) },
      'question-loading': { label: getPhaseLabel(instance), pct: Math.min(15 + instance.questionCount * 14, 85) },
      email: { label: t.phaseReady || 'Your consultation is almost ready', pct: 90 },
      'assessment-loading': { label: t.phaseGenerating || 'Preparing your hair and scalp consultation', pct: 95 }
    };

    const phase = phases[instance.currentStep] || phases.anchor;
    if (instance.progressBar) instance.progressBar.style.setProperty('--progress', `${phase.pct}%`);
    if (instance.progressEl) instance.progressEl.setAttribute('aria-valuenow', Math.round((phase.pct / 100) * 7));
    if (instance.phaseEl) {
      instance.phaseEl.textContent = phase.label;
      instance.phaseEl.style.opacity = phase.label ? '1' : '0';
    }
  }

  function getPhaseLabel(instance) {
    const t = instance.config?.i18n || {};
    if (instance.questionCount <= 2) return t.phaseUnderstanding || 'Understanding your hair and scalp';
    if (instance.questionCount <= 4) return t.phaseDiagnosing || 'Building a clearer view of your hair and scalp';
    return t.phaseRefining || 'Refining your consultation';
  }

  function normalizeCopy(instance, value) {
    void instance;
    return typeof value === 'string' ? value.trim() : '';
  }

  function normalizeCopyToken(instance, value) {
    return normalizeCopy(instance, value)
      .toLowerCase()
      .replace(/[^\w\u0600-\u06FF ]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function setOptionalCopy(instance, el, value) {
    if (!el) return '';
    const normalized = normalizeCopy(instance, value);
    el.textContent = normalized;
    el.hidden = !normalized;
    return normalized;
  }

  function renderAdaptiveCopy(instance, data) {
    const progressEl = instance.querySelector('[data-adaptive-progress]');
    const reflectionEl = instance.querySelector('[data-adaptive-reflection]');
    const whyEl = instance.querySelector('[data-adaptive-why]');
    const contextEl = instance.querySelector('[data-adaptive-context]');
    const supportEl = instance.querySelector('[data-adaptive-support]');
    const bridgeEl = instance.querySelector('[data-adaptive-bridge]');
    const insightEl = instance.querySelector('[data-adaptive-insight]');
    const questionEl = instance.querySelector('[data-adaptive-question]');
    const seen = new Set();
    const dedupe = (value) => {
      const normalized = normalizeCopy(instance, value);
      if (!normalized) return '';
      const token = normalizeCopyToken(instance, normalized);
      if (token && seen.has(token)) return '';
      if (token) seen.add(token);
      return normalized;
    };

    const progressCopy = dedupe(data.progress_copy);
    const listeningReflection = dedupe(data.listening_reflection);
    const whyThisQuestion = dedupe(data.why_this_question);
    const questionContext = dedupe(data.question_context);
    const bridgeText = dedupe(data.bridge_text);
    const microInsight = dedupe(data.micro_insight);

    setOptionalCopy(instance, progressEl, progressCopy);
    setOptionalCopy(instance, reflectionEl, listeningReflection);

    const renderedWhy = setOptionalCopy(instance, whyEl, whyThisQuestion);
    const renderedContext = setOptionalCopy(instance, contextEl, questionContext);
    if (supportEl) supportEl.hidden = !(renderedWhy || renderedContext);

    setOptionalCopy(instance, bridgeEl, bridgeText);
    setOptionalCopy(instance, insightEl, microInsight);
    if (questionEl) questionEl.textContent = normalizeCopy(instance, data.question_text);
  }

  function renderAdaptiveQuestion(instance, data) {
    instance._log('render-question', {
      questionId: data.question_id,
      inputType: data.input_type,
      optionCount: (data.options || []).length
    });
    clearErrorStates(instance);

    instance._pendingBridgeText = normalizeCopy(instance, data.bridge_text) || null;

    const progressEl = instance.querySelector('[data-adaptive-progress]');
    const reflectionEl = instance.querySelector('[data-adaptive-reflection]');
    const whyEl = instance.querySelector('[data-adaptive-why]');
    const supportEl = instance.querySelector('[data-adaptive-support]');
    const insightEl = instance.querySelector('[data-adaptive-insight]');
    const contextEl = instance.querySelector('[data-adaptive-context]');
    const bridgeEl = instance.querySelector('[data-adaptive-bridge]');
    const questionEl = instance.querySelector('[data-adaptive-question]');
    const optionsEl = instance.querySelector('[data-adaptive-options]');
    const adaptiveTextBlock = instance.querySelector('[data-adaptive-text]');
    const adaptiveTextarea = instance.querySelector('[data-adaptive-textarea]');
    const adaptiveHint = instance.querySelector('[data-adaptive-hint]');
    const adaptiveSuggestions = instance.querySelector('[data-adaptive-suggestions]');

    if (optionsEl) {
      optionsEl.innerHTML = '';
      optionsEl.hidden = true;
    }
    if (adaptiveTextBlock) adaptiveTextBlock.hidden = true;
    if (adaptiveTextarea) {
      adaptiveTextarea.value = '';
      adaptiveTextarea.style.height = '';
      delete adaptiveTextarea.dataset.selectedSuggestion;
    }
    if (adaptiveHint) adaptiveHint.hidden = true;
    if (adaptiveSuggestions) adaptiveSuggestions.hidden = true;
    instance._renderAdaptiveSuggestions([]);
    if (progressEl) {
      progressEl.textContent = '';
      progressEl.hidden = true;
    }
    if (reflectionEl) {
      reflectionEl.textContent = '';
      reflectionEl.hidden = true;
    }
    if (whyEl) {
      whyEl.textContent = '';
      whyEl.hidden = true;
    }
    if (supportEl) supportEl.hidden = true;
    if (insightEl) {
      insightEl.textContent = '';
      insightEl.hidden = true;
    }
    if (contextEl) {
      contextEl.textContent = '';
      contextEl.hidden = true;
    }
    if (bridgeEl) {
      bridgeEl.textContent = '';
      bridgeEl.hidden = true;
    }
    if (questionEl) questionEl.textContent = '';

    renderAdaptiveCopy(instance, data);

    const wantsFreeText = data.input_type === 'free_text';
    const hasOptions = (data.options || []).length > 0;

    if (hasOptions && !wantsFreeText) {
      const letters = ['A', 'B', 'C', 'D', 'E', 'F'];
      if (optionsEl) {
        optionsEl.innerHTML = (data.options || []).map((opt, i) =>
          `<button class="consultation-option" type="button" data-value="${instance._escapeAttr(opt.value)}" role="radio" aria-checked="false">
            <span class="consultation-option__key">${letters[i] || ''}</span>
            <span class="consultation-option__text">${instance._escapeHtml(opt.label)}</span>
          </button>`
        ).join('');
        optionsEl.hidden = false;
      }

      if (instance.kbdHint) {
        const keys = letters.slice(0, (data.options || []).length).join(', ');
        const hintTemplate = instance.config?.i18n?.keyboardSelectHint || 'Press {{ keys }} to select';
        instance.kbdHint.textContent = hintTemplate.replace('{{ keys }}', keys);
      }

      if (adaptiveTextBlock) adaptiveTextBlock.hidden = true;
    } else if (wantsFreeText) {
      if (adaptiveTextBlock) {
        adaptiveTextBlock.hidden = false;
        if (adaptiveTextarea) {
          adaptiveTextarea.value = '';
          if (data.placeholder) adaptiveTextarea.placeholder = data.placeholder;
          instance._autoResizeTextarea(adaptiveTextarea);
          setTimeout(() => adaptiveTextarea.focus(), 100);
        }
      }
      instance._renderAdaptiveSuggestions(data.suggested_answers || []);
      instance._syncAdaptiveTextControls();
      if (optionsEl) {
        optionsEl.innerHTML = '';
        optionsEl.hidden = true;
      }
      if (instance.kbdHint) instance.kbdHint.textContent = '';
    }

    instance._renderDebug(data);
  }

  function renderVoiceRetry(instance, data) {
    const progressEl = instance.querySelector('[data-adaptive-progress]');
    const reflectionEl = instance.querySelector('[data-adaptive-reflection]');
    const supportEl = instance.querySelector('[data-adaptive-support]');
    const whyEl = instance.querySelector('[data-adaptive-why]');
    const insightEl = instance.querySelector('[data-adaptive-insight]');
    const contextEl = instance.querySelector('[data-adaptive-context]');
    const bridgeEl = instance.querySelector('[data-adaptive-bridge]');
    const questionEl = instance.querySelector('[data-adaptive-question]');
    const optionsEl = instance.querySelector('[data-adaptive-options]');
    const adaptiveTextBlock = instance.querySelector('[data-adaptive-text]');
    const adaptiveTextarea = instance.querySelector('[data-adaptive-textarea]');
    const adaptiveHint = instance.querySelector('[data-adaptive-hint]');
    const adaptiveSuggestions = instance.querySelector('[data-adaptive-suggestions]');

    if (optionsEl) {
      optionsEl.innerHTML = '';
      optionsEl.hidden = true;
    }
    if (progressEl) {
      progressEl.textContent = '';
      progressEl.hidden = true;
    }
    if (reflectionEl) {
      reflectionEl.textContent = '';
      reflectionEl.hidden = true;
    }
    if (whyEl) {
      whyEl.textContent = '';
      whyEl.hidden = true;
    }
    if (supportEl) supportEl.hidden = true;
    if (insightEl) {
      insightEl.textContent = '';
      insightEl.hidden = true;
    }
    if (contextEl) {
      contextEl.textContent = '';
      contextEl.hidden = true;
    }
    if (bridgeEl) {
      bridgeEl.textContent = '';
      bridgeEl.hidden = true;
    }
    if (questionEl) questionEl.textContent = '';
    if (adaptiveHint) adaptiveHint.hidden = true;
    if (adaptiveSuggestions) adaptiveSuggestions.hidden = true;
    instance._renderAdaptiveSuggestions([]);

    renderAdaptiveCopy(instance, data);

    if (adaptiveTextBlock) {
      adaptiveTextBlock.hidden = false;
      if (adaptiveTextarea) {
        adaptiveTextarea.value = '';
        adaptiveTextarea.style.height = '';
        delete adaptiveTextarea.dataset.selectedSuggestion;
        if (data.placeholder) adaptiveTextarea.placeholder = data.placeholder;
        instance._autoResizeTextarea(adaptiveTextarea);
        setTimeout(() => adaptiveTextarea.focus(), 100);
      }
    }

    instance._renderAdaptiveSuggestions(data.suggested_answers || data.voice_retry_suggestions || []);
    instance._syncAdaptiveTextControls();

    instance._lastAdaptiveResponse = data;
    instance._lastQuestionComplete = false;

    if (instance.conversationHistory.length > 0) {
      const lastTurn = instance.conversationHistory[instance.conversationHistory.length - 1];
      if (lastTurn.input_type === 'voice_note' && !lastTurn.answer) {
        instance.conversationHistory.pop();
        instance.questionCount = Math.max(0, instance.questionCount - 1);
        instance._setConversationHistory(instance.conversationHistory);
      }
    }
  }

  function setInflightUI(instance, active) {
    const activeStep = instance.querySelector(`.consultation-step[data-step="${instance.currentStep}"]`);
    if (!activeStep) return;

    activeStep.querySelectorAll('.consultation-options').forEach((el) => {
      el.style.pointerEvents = active ? 'none' : '';
      el.style.opacity = active ? '0.6' : '';
    });
    activeStep.querySelectorAll('[data-intake-submit]').forEach((btn) => {
      const intakeText = activeStep.querySelector('[data-intake-text]');
      btn.disabled = active || !(intakeText && intakeText.value.trim());
    });
    activeStep.querySelectorAll('[data-adaptive-text-submit]').forEach((btn) => {
      const adaptiveTextarea = activeStep.querySelector('[data-adaptive-textarea]');
      btn.disabled = active || !(adaptiveTextarea && adaptiveTextarea.value.trim());
    });
    activeStep.querySelectorAll('[data-intake-text], [data-adaptive-textarea]').forEach((ta) => {
      ta.disabled = active;
    });
    activeStep.querySelectorAll('[data-contact-input], [data-contact-skip], [data-email-form] button[type="submit"]').forEach((el) => {
      el.disabled = !!active;
    });
  }

  function showBridgeText(instance) {
    const bridgeEl = instance.querySelector('[data-bridge-text]');
    if (!bridgeEl) return;

    if (instance._pendingBridgeText) {
      bridgeEl.textContent = instance._pendingBridgeText;
      bridgeEl.hidden = false;
    } else {
      bridgeEl.textContent = '';
      bridgeEl.hidden = true;
    }
  }

  function clearErrorStates(instance) {
    instance.querySelectorAll('.consultation-error').forEach((element) => element.remove());
    instance.querySelectorAll('.consultation-loading.is-error, .consultation-wait.is-error').forEach((element) => {
      element.classList.remove('is-error');
    });
  }

  function getQuestionRetryLabel(instance) {
    const t = instance.config?.i18n || {};
    return normalizeCopy(instance, t.errorRetryTryAgain) || 'Try again';
  }

  function getFriendlyErrorMessage(instance, error) {
    const code = String(error?.errorCode || error?.code || error?.error_code || '').trim();
    if (code && window.TaraConsultationApi && typeof window.TaraConsultationApi.friendlyError === 'function') {
      return window.TaraConsultationApi.friendlyError(error, instance.config?.i18n || {});
    }
    return '';
  }

  function getQuestionErrorMessage(instance, error) {
    const friendly = getFriendlyErrorMessage(instance, error);
    if (friendly) return friendly;
    const t = instance.config?.i18n || {};
    return normalizeCopy(instance, t.questionUnavailable)
      || 'We could not continue right now. Try again and we will continue from the same path.';
  }

  function getAssessmentErrorMessage(instance, error) {
    const friendly = getFriendlyErrorMessage(instance, error);
    if (friendly) return friendly;
    const t = instance.config?.i18n || {};
    return normalizeCopy(instance, t.assessmentUnavailable)
      || 'We could not finish your consultation right now. Try again.';
  }

  function renderQuestionRequestError(instance, direction, error) {
    renderErrorState(instance, getQuestionErrorMessage(instance, error), {
      stepName: 'question-loading',
      actionLabel: getQuestionRetryLabel(instance),
      onAction: () => instance._fetchNextQuestion(direction || 'forward'),
    });
  }

  function renderAssessmentFailure(instance, onRetry, error) {
    renderErrorState(instance, getAssessmentErrorMessage(instance, error), {
      stepName: 'assessment-loading',
      actionLabel: getQuestionRetryLabel(instance),
      onAction: () => {
        if (typeof onRetry === 'function') onRetry();
      },
    });
  }

  function renderErrorState(instance, message, options) {
    const { stepName = instance.currentStep, actionLabel, onAction } = options || {};
    const step = stepName
      ? instance.querySelector(`[data-step="${stepName}"] .consultation-step__inner`)
      : instance.querySelector('.consultation-step:not([hidden]) .consultation-step__inner');
    if (!step) return;
    const retryLabel = actionLabel || instance.config?.i18n?.errorRetry || 'Start over';
    step.querySelector('.consultation-error')?.remove();
    const loadingSurface = step.querySelector('.consultation-loading, .consultation-wait');
    if (loadingSurface) loadingSurface.classList.add('is-error');

    const errorEl = document.createElement('div');
    errorEl.className = 'consultation-error';

    const textEl = document.createElement('p');
    textEl.className = 'consultation-error__text';
    textEl.textContent = message;

    const retryBtn = document.createElement('button');
    retryBtn.className = 'consultation-error__retry';
    retryBtn.type = 'button';
    retryBtn.textContent = retryLabel;
    retryBtn.addEventListener('click', () => {
      errorEl.remove();
      if (loadingSurface) loadingSurface.classList.remove('is-error');
      if (typeof onAction === 'function') {
        onAction();
        return;
      }
      instance._retake();
    });

    errorEl.append(textEl, retryBtn);
    step.appendChild(errorEl);
  }

  window.TaraConsultationUI = Object.freeze({
    showStep,
    disableStepControls,
    enableStepControls,
    goBack,
    updateProgress,
    getPhaseLabel,
    normalizeCopy,
    normalizeCopyToken,
    setOptionalCopy,
    renderAdaptiveCopy,
    renderAdaptiveQuestion,
    renderVoiceRetry,
    setInflightUI,
    showBridgeText,
    clearErrorStates,
    getQuestionRetryLabel,
    getQuestionErrorMessage,
    getAssessmentErrorMessage,
    renderQuestionRequestError,
    renderAssessmentFailure,
    renderErrorState,
  });
})();
